import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Snackbar } from '@mui/material';
import ArrowIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import HeartIcon from '@mui/icons-material/FavoriteBorderOutlined';
import HandsIcon from '@mui/icons-material/VolunteerActivismOutlined';
import GiftIcon from '@mui/icons-material/CardGiftcardOutlined';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import MailIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { apiPost } from '../api/readerApi';
import { useSite } from '../site/SiteProvider';
import { useTranslation } from 'react-i18next';
import './contacto.css';

/**
 * CONTACTO.
 *
 * La pagina de contacto de casi todas las fundaciones es un formulario y un
 * correo. Esta pregunta primero PARA QUE ESCRIBE, porque cada motivo va a un
 * sitio distinto y necesita cosas distintas — y porque preguntarlo cambia lo
 * que la gente escribe: quien elige «quiero contar lo que me paso» cuenta, y
 * quien elige «tengo un problema con mi pago» va al grano.
 *
 * 🔴 EL MOTIVO QUE JUSTIFICA LA PAGINA ENTERA es «soy canalizador». Es la
 * puerta de los permisos, y hoy los permisos son lo que bloquea el plan 4 (las
 * 5.778 del corpus) y los nombres de la pagina de Gracias. Por eso ese motivo
 * no esta escondido en una lista: tiene su propio texto y se dirige a ellos.
 */

const HTTP_BAD_REQUEST = 400;
const EMPTY_FORM = { nombre: '', correo: '', mensaje: '', website: '' };

const MOTIVOS = [
  {
    id: 'testimonio',
    icono: <HeartIcon />,
    titulo: 'contacto.testimonio_titulo',
    /* Todas las frases pasan a PEDIR algo, no a describir el motivo (Faina,
       29-ago). La anterior contaba lo que le había pasado al lector —«leíste una
       canalización y te movió algo»— y encima le decía lo que a nosotros nos
       importa. Esta le pregunta a él. */
    que: 'contacto.testimonio_que',
    campo: 'contacto.testimonio_campo',
    asunto: 'contacto.testimonio_asunto'
  },
  {
    /* Faina (02-oct): «crea una "Me gustaría saber más"… va debajo de quiero
       donar»; luego «pasa a 2». Para quien no viene a hacer nada todavía: solo a preguntar. */
    id: 'saber',
    icono: <InfoIcon />,
    titulo: 'contacto.saber_titulo',
    que: 'contacto.saber_que',
    asunto: 'contacto.saber_asunto'
  },
  /* «Soy canalizador» salió el 02-oct (Faina: «Soy canalizador fuera»). Sus
     textos siguen en la tabla (contacto.canalizador_*) por si vuelve. */
  {
    id: 'colaborar',
    icono: <HandsIcon />,
    /* 🔴 «SER PARTE», NO «COLABORAR» (Faina, 29-ago). No es lo mismo: colaborar
       es echar una mano en algo de otro; ser parte es pertenecer. Y quien
       escribe por aquí no viene a hacer un favor — viene porque algo de esto le
       tocó y quiere estar dentro. La frase de debajo se queda: dice lo que se
       puede hacer, que es lo que la persona no sabe. */
    titulo: 'contacto.colaborar_titulo',
    /* La frase es de Faina (29-ago). Antes enumeraba lo que hace falta —traducir,
       prestar la voz, corregir— y eso acotaba la respuesta a nuestra lista.
       Preguntando en abierto, cada uno ofrece lo que de verdad sabe hacer, que
       puede ser algo en lo que no habíamos pensado. */
    que: 'contacto.colaborar_que',
    campo: 'contacto.colaborar_campo',
    asunto: 'contacto.colaborar_asunto'
  },
  {
    id: 'donar',
    icono: <GiftIcon />,
    /* 🔴 SOLO DONAR — LA BECA NO VA AQUI (Faina, 29-ago: «la beca no tiene nada
       que ver con lo que nosotros recibimos por donación»). Tenia razon: una
       cosa es lo que entra a la Fundacion y otra lo que la Fundacion concede.
       Meterlas en la misma puerta obligaba a quien pide a entrar por la puerta
       de quien da.
       ⚠️ Y hoy donar EMPIEZA POR ESCRIBIR: no hay pasarela ni cuenta publicada,
       asi que el texto no promete un boton que no existe. */
    titulo: 'contacto.donar_titulo',
    /* 🔴 LAS DOS FORMAS, Y EL TERRENO PRIMERO EN IMPORTANCIA (Faina, 29-ago:
       «recibimos donaciones en dinero y en terrenos, para la conservación»).
       Se dice QUE se puede donar antes que COMO, porque casi nadie imagina que
       puede donar tierra a una fundación — y esa es la mitad de la misión, no
       un añadido.
       ⚠️ Y sin «por favor escríbenos y te decimos cómo» (Faina, 29-ago: «la
       última frase es redundante»). Lo era: se está en la página de contacto,
       con el formulario justo debajo — pedir que escriba es pedirle lo que ya
       está haciendo. Las otras frases sí piden algo, pero piden CONTENIDO —qué
       se movió en ti, cómo crees que puedes ayudar—, no el gesto obvio. */
    /* La forma es de Faina (29-ago). Pasa a imperativo como las otras tres
       —cuéntanos, envíanos, haz— y así las seis puertas piden en vez de
       describir. Los dos puntos resuelven la asimetría de la frase: sin ellos,
       «haz tu donación en dinero o un terreno» encadena mal las dos cosas.
       Y «la conservación», no «conservar» (Faina, 29-ago): el verbo deja la
       pregunta colgando —conservar qué—, y además «conservación» es la palabra
       que ya usan La Fundación y la Def 71, así que quien la lee en dos sitios
       entiende que es lo mismo. */
    /* Sin coma antes de «o» (Faina lo preguntó, 29-ago): con dos elementos
       cortos y paralelos la disyuntiva no la lleva. */
    que: 'contacto.donar_que',
    campo: 'contacto.donar_campo',
    asunto: 'contacto.donar_asunto'
  },
  {
    id: 'cuenta',
    icono: <HelpIcon />,
    titulo: 'contacto.cuenta_titulo',
    que: 'contacto.cuenta_que',
    asunto: 'contacto.cuenta_asunto'
  },
  /* «Prensa» salió el 02-oct (Faina: «Fuera prensa»). Sus textos siguen en
     la tabla (contacto.prensa_*) por si vuelve. */
  /* «¿Algo no suena bien en tu idioma?» salió el 02-oct (Faina: «elimina el
     último»). Era la puerta de la revisión nativa gratuita (15-sep); sus textos
     siguen en la tabla (contacto.idioma_*) por si vuelve. */
];

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const sitio = useSite();
  /* 🔴 EL MOTIVO VIVE EN LA DIRECCION, NO EN EL ESTADO (Faina, 29-ago: «cuando
     estoy en contacto y vuelvo atrás, me manda al registro»).

     Estaba en `useState`, y elegir un motivo es un PASO VISIBLE —la página
     cambia entera— que el navegador no conocía: al pulsar «atrás» no deshacía
     ese paso, sino que sacaba de /contacto a la página anterior. Si venías del
     registro, aparecías en el registro.

     Con el motivo en la URL, «atrás» hace lo que el lector espera: volver a la
     lista. Y de regalo, un enlace como `/contacto?motivo=canalizador` lleva
     directo a su formulario — útil para escribir a los canalizadores.

     ⚠️ Regla: si un clic cambia lo que se ve, tiene que cambiar la dirección.
     Si no, el botón «atrás» miente. */
  const [params, setParams] = useSearchParams();
  const motivo = params.get('motivo');

  const setMotivo = (id) => {
    const p = new URLSearchParams(window.location.search);
    if (id) p.set('motivo', id); else p.delete('motivo');
    setParams(p);   // entra en el historial: «atrás» deshace este paso
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [aviso, setAviso] = useState('');
  const [datos, setDatos] = useState(EMPTY_FORM);
  const [enviando, setEnviando] = useState(false);

  const elegido = MOTIVOS.find((m) => m.id === motivo);

  const enviar = async (e) => {
    e.preventDefault();
    /* El motivo «idioma» añade de que idioma y pagina habla el lector: sin eso
       una frase suelta no se encuentra entre 679 textos por 55 idiomas. */
    const contexto = elegido && elegido.id === 'idioma'
      ? `\n\n${t('contacto.idioma_datos', { idioma: i18n.language, pagina: params.get('pagina') || '/' })}`
      : '';
    const asunto = elegido ? t(elegido.asunto) : t('menu.contacto');
    setEnviando(true);
    const { ok, status } = await apiPost('/contact', {
      site: sitio.site,
      language: i18n.language,
      name: datos.nombre,
      email: datos.correo,
      subject: asunto,
      message: `${datos.mensaje}${contexto}`,
      website: datos.website
    });
    setEnviando(false);
    if (ok) {
      setDatos(EMPTY_FORM);
      setAviso(t('contacto.enviado'));
    } else {
      setAviso(t(status === HTTP_BAD_REQUEST ? 'contacto.error_datos' : 'contacto.error_envio'));
    }
  };

  return (
    <Box className="con">
      <Container maxWidth="md">

        <Box className="con-cabecera">
          <Typography variant="h1" className="con-h1">{t('menu.escribenos')}</Typography>
          {/* Sin bajada (Faina, 29-ago). Decía «dinos primero para qué escribes;
              así llega a quien corresponde y te contestamos antes» — y las seis
              puertas de abajo ya lo enseñan solas: se ve que hay que elegir una
              y se ve a qué lleva cada una. Explicar lo que ya se ve es hacer
              leer de más. */}
        </Box>

        {!elegido && (
          <Box className="con-motivos">
            {MOTIVOS.map((m) => (
              <button
                type="button"
                key={m.id}
                className={`con-motivo${m.id === 'canalizador' ? ' con-motivo-destacado' : ''}`}
                onClick={() => setMotivo(m.id)}
              >
                <span className="con-motivo-icono">{m.icono}</span>
                <span className="con-motivo-texto">
                  <span className="con-motivo-titulo">{t(m.titulo)}</span>
                  <span className="con-motivo-que">{t(m.que)}</span>
                </span>
                <ArrowIcon className="con-motivo-flecha" fontSize="small" />
              </button>
            ))}
          </Box>
        )}

        {elegido && (
          <Box className="con-formulario">
            <button type="button" className="con-volver" onClick={() => setMotivo(null)}>
              <ArrowBackIcon fontSize="inherit" /> {t('contacto.otro_motivo')}
            </button>

            <Typography variant="h2" className="con-h2">{t(elegido.titulo)}</Typography>
            {/* 🔴 EL FILO NARANJA, EN LAS SEIS (Faina, 29-ago). Primero fue solo
                para «Soy canalizador» —la línea que quedó de la nota borrada— y
                al verla pidió ponerla en todas. Tenía razón: desde que las
                frases PIDEN algo, las seis son un recado y no una descripción,
                así que marcar una sola dejaba a las otras cinco flotando sueltas
                encima del formulario. La línea las ancla a todas. */}
            <Typography className="con-que con-que-marcado">{t(elegido.que)}</Typography>

            <Box component="form" onSubmit={enviar} className="con-campos">
              <TextField
                label={t('contacto.tu_nombre')} value={datos.nombre} required
                onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
                variant="outlined" fullWidth
              />
              <TextField
                label={t('contacto.tu_correo')} type="email" value={datos.correo} required
                onChange={(e) => setDatos({ ...datos, correo: e.target.value })}
                variant="outlined" fullWidth
              />
              {/* 🔴 LA CAJA SE LLAMA COMO LO QUE SE PIDE (29-ago). Las cuatro
                  primeras puertas PIDEN algo —qué se movió en ti, cómo puedes
                  ayudar— y luego el formulario decía «Tu mensaje» en las seis:
                  la pregunta se hacía arriba y abajo se olvidaba.
                  Con el nombre del campo repitiendo la pregunta, el formulario
                  sigue hablando en vez de volverse genérico. Los dos motivos de
                  trámite —cuenta y prensa— se quedan con «Tu mensaje», que es
                  lo que toca ahí. */}
              <TextField
                label={elegido.campo ? t(elegido.campo) : t('contacto.tu_mensaje')} value={datos.mensaje} required
                onChange={(e) => setDatos({ ...datos, mensaje: e.target.value })}
                variant="outlined" fullWidth multiline minRows={5}
              />
              <input
                type="text" name="website" value={datos.website} tabIndex={-1} autoComplete="off"
                aria-hidden="true" className="con-trampa"
                onChange={(e) => setDatos({ ...datos, website: e.target.value })}
              />
              <Button type="submit" className="con-enviar" disabled={enviando}>
                {enviando ? t('contacto.enviando') : t('contacto.enviar')}
              </Button>
              <Typography className="con-aviso">
                {t('contacto.aviso_correo')}
              </Typography>
            </Box>
          </Box>
        )}

        <Box className="con-directo">
          <Typography className="con-rotulo">{t('contacto.o_directamente')}</Typography>
          <Box className="con-directo-enlaces">
            <a href={`mailto:${sitio.email}`} className="con-directo-enlace">
              <MailIcon fontSize="small" /> {sitio.email}
            </a>
            {sitio.whatsapp && sitio.whatsapp.url && (
              <a href={sitio.whatsapp.url} target="_blank" rel="noopener noreferrer"
                 className="con-directo-enlace">
                <WhatsAppIcon fontSize="small" /> {sitio.whatsapp.number}
              </a>
            )}
          </Box>
          {sitio.address && (
            <Typography className="con-donde">
              {sitio.legalName} · {sitio.address}
            </Typography>
          )}
        </Box>

        <Snackbar
          open={Boolean(aviso)} autoHideDuration={5000}
          onClose={() => setAviso('')} message={aviso}
        />

      </Container>
    </Box>
  );
};

export default ContactPage;
